import { FC, useCallback, useMemo, useState } from 'react';
import Cookie from 'js-cookie';
import { useDispatch, useSelector } from 'react-redux';
import { AnyAction } from 'redux';
import { ThunkDispatch } from 'redux-thunk';
import { Router, useRouter } from 'shared-routing';
import {
  SignInFormMode,
  SignInModalErrors,
  SignInModalLabels,
  SignInModalValues,
  SignInTemplate,
  SignUpValues,
} from 'voyager';

import { Routes } from '@common/constants';
import {
  SignUpErrorKeys,
  buildSignUpPayload,
  mapSignUpError,
  translateSignUpErrors,
  validateSignUp,
} from '@features/SignIn/helpers/signUp';
import { useTranslations } from '@lang';
import { fetchBasketDetails } from '@store/basket/actions';
import { entitySelectors } from '@store/entity/selectors';
import { createGuest, signIn } from '@store/guest/actions';
import { useSignInSession } from '@features/SignIn/hooks/useSignInSession';

import { SignInV2Props } from './SignInV2.types';

type AppDispatch = ThunkDispatch<unknown, unknown, AnyAction>;

export const SignInV2: FC<SignInV2Props> = ({ property, language }) => {
  const { t } = useTranslations();
  const router = useRouter();
  const dispatch = useDispatch<AppDispatch>();

  const homepage = useSelector((state) => {
    const propertyHomepage = entitySelectors.getProperty(state, property.id)?.homepage;
    return propertyHomepage ? entitySelectors.getHomepage(state, propertyHomepage.id) : null;
  });

  const mode: SignInFormMode = router.pathname === Routes.REGISTER ? 'signUp' : 'signIn';

  useSignInSession();

  const handleModeChange = useCallback(
    (nextMode: SignInFormMode) => {
      const pathname = nextMode === 'signUp' ? Routes.REGISTER : Routes.SIGNIN;
      const { redirectTo } = router.query;
      router.push({ pathname, query: redirectTo ? { redirectTo } : undefined });
    },
    [router]
  );

  const [values, setValues] = useState<SignInModalValues>({ email: '', password: '' });
  const [errors, setErrors] = useState<SignInModalErrors | undefined>(undefined);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = useCallback((field: keyof SignInModalValues, value: string) => {
    setValues((prev) => ({ ...prev, [field]: value }));
    setErrors(undefined);
  }, []);

  const handleSubmit = useCallback(async () => {
    setIsSubmitting(true);
    setErrors(undefined);
    try {
      await dispatch(signIn(property.id, values));
      const redirectTo = router.query.redirectTo?.toString();
      const basketId = Cookie.get('basketId');
      if (basketId) {
        try {
          await dispatch(fetchBasketDetails(property.id, basketId, language));
        } catch (e) {}
      }
      await Router.push(redirectTo || Routes.HOME);
    } catch (e) {
      setErrors({ form: t('error.login_failed') });
    } finally {
      setIsSubmitting(false);
    }
  }, [dispatch, property.id, values, router.query.redirectTo, language, t]);

  const [signUpValues, setSignUpValues] = useState<SignUpValues>({
    email: '',
    isSpaMember: false,
    spaMemberNumber: '',
  });
  const [signUpErrorKeys, setSignUpErrorKeys] = useState<SignUpErrorKeys | undefined>(undefined);
  const [isSigningUp, setIsSigningUp] = useState(false);
  const [signUpSuccess, setSignUpSuccess] = useState(false);

  const handleSignUpChange = useCallback(
    <K extends keyof SignUpValues>(field: K, value: SignUpValues[K]) => {
      setSignUpValues((prev) => ({ ...prev, [field]: value }));
      setSignUpErrorKeys(undefined);
    },
    []
  );

  const handleSignUpSubmit = useCallback(async () => {
    const validationErrorKeys = validateSignUp(signUpValues, property.isSpaIntegrated);
    if (validationErrorKeys) {
      setSignUpErrorKeys(validationErrorKeys);
      return;
    }

    setIsSigningUp(true);
    setSignUpErrorKeys(undefined);
    try {
      await dispatch(
        createGuest(property.id, buildSignUpPayload(signUpValues, property.isSpaIntegrated))
      );
      setSignUpSuccess(true);
    } catch (e) {
      setSignUpErrorKeys(mapSignUpError(e));
    } finally {
      setIsSigningUp(false);
    }
  }, [dispatch, property.id, property.isSpaIntegrated, signUpValues]);

  const signUpErrors = useMemo(
    () => signUpErrorKeys && translateSignUpErrors(signUpErrorKeys, t),
    [signUpErrorKeys, t]
  );

  const handleForgottenPasswordClick = useCallback(() => {
    router.push({ pathname: Routes.FORGOTTEN_PASSWORD });
  }, [router]);

  const labels: SignInModalLabels = useMemo(
    () => ({
      title: t('signIn.v2.title'),
      subtitle: t('signIn.v2.subtitle'),
      signUpPrompt: t('signIn.v2.signUpPrompt'),
      signUpLink: t('signIn.v2.signUpLink'),
      emailLabel: t('signIn.v2.emailLabel'),
      passwordLabel: t('signIn.v2.passwordLabel'),
      submit: t('signIn.v2.submit'),
      forgottenPassword: t('signIn.v2.forgottenPassword'),
      signUpTitle: t('signIn.v2.signUpTitle'),
      signUpSubtitle: t('signIn.v2.signUpSubtitle'),
      signInPrompt: t('signIn.v2.signInPrompt'),
      signInLink: t('signIn.v2.signInLink'),
      isSpaMember: t('signIn.v2.isSpaMember'),
      spaMemberNumberLabel: t('signIn.v2.spaMemberNumberLabel'),
      signUpSubmit: t('signIn.v2.signUpSubmit'),
    }),
    [t]
  );

  return (
    <SignInTemplate
      propertyName={property.name}
      propertyLogoUrl={property.logo?.url}
      heroImageUrl={homepage?.heroBackgroundImage?.url}
      values={values}
      onChange={handleChange}
      onSubmit={handleSubmit}
      isSubmitting={isSubmitting}
      errors={errors}
      signUpValues={signUpValues}
      onSignUpChange={handleSignUpChange}
      onSignUpSubmit={handleSignUpSubmit}
      isSigningUp={isSigningUp}
      signUpErrors={signUpErrors}
      showSpaMemberOption={property.isSpaIntegrated}
      onForgottenPasswordClick={handleForgottenPasswordClick}
      signUpSuccessMessage={signUpSuccess ? t('signIn.v2.signUpSuccessMessage') : undefined}
      mode={mode}
      onModeChange={handleModeChange}
      labels={labels}
    />
  );
};
